import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Avatar,
  Button,
  Chip,
  Paper,
  Dialog,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { VendorProfile as VendorProfileAction } from "../../../Redux/action/VendorProfileAction";
import Loader from "../../Common/Loader/loader";
import { IoEyeOutline } from "react-icons/io5";
import CloseIcon from "@mui/icons-material/Close";

const ViewProfile = ({ handleChange }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.VendorProfile?.result);
  const { userResult } = useSelector((state) => state.Auth);
  const id = userResult?.vendorid;

  const [state, setState] = useState({});
  const [locations, setLocations] = useState([]);
  const [dialogPreview, setDialogPreview] = useState(null);

  const isIndian = userInfo?.countryCode === "in";

  const getPreviewType = (url) => {
    if (!url) return "unknown";
    const ext = url.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "webp"].includes(ext)) return "image";
    if (ext === "pdf") return "pdf";
    return "unknown";
  };

  useEffect(() => {
    if (userInfo?.vendorId) {
      setState({
        vendorId: userInfo?.vendorId,
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
        vendorEmailId: userInfo?.email,
        mobile: userInfo?.mobile,
        companyWebsite: userInfo?.companyWebsite,
        vendorEmpId: userInfo?.vendorEmpId,
        city: userInfo?.city,
        country: userInfo?.country,
        dialCode: userInfo?.dialCode,
        address: userInfo?.address,
        description: userInfo?.description,
        company: userInfo?.company,
        gstNumber: userInfo?.gstNumber,
        panNumber: userInfo?.panNumber,
        governmentId: userInfo?.governmentId,
        governmentTaxId: userInfo?.governmentTaxId,
      });
    }
    setLocations(userInfo?.locations || []);
  }, [userInfo]);

  useEffect(() => {
    dispatch(VendorProfileAction(id));
  }, [dispatch, id]);

  if (!state?.vendorId) {
    return (
      <div
        style={{
          height: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Grid
        container
        spacing={3}
        sx={{ p: 2, margin: "auto" }}
        className="ven_dash_wrapper"
      >
        {/* Profile Details */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardHeader
              title="Profile Details"
              style={{ color: "#286a94" }}
              action={
                <Button
                  variant="contained"
                  onClick={handleChange}
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: 600,
                    backgroundColor: "#286a94",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#1e5474",
                    },
                  }}
                >
                  Edit Profile
                </Button>
              }
            />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                {/* Profile */}
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: 140,
                      height: 140,
                      borderRadius: "50%",
                      border: "4px solid #286a94",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      boxShadow: "0px 4px 10px rgba(0,0,0,0.15)",
                    }}
                  >
                    <Avatar
                      src={userInfo?.vendorProfileImageUrl}
                      sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                      }}
                      imgProps={{
                        loading: "eager",
                        decoding: "async",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      maxWidth: { xs: "180px", sm: "220px", md: "260px" },
                      margin: "0 auto",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      color: "#0E3F6F",
                    }}
                  >
                    {state.firstName} {state.lastName}
                  </Typography>
                  <Tooltip title={"VENDOR ID"}>
                    <Typography
                      sx={{
                        mt: 1,
                        fontWeight: 600,
                        background: "#286a94",
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        px: 2,
                        py: 0.8,
                        borderRadius: "10px",
                        fontSize: "13px",
                        maxWidth: "210px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                      }}
                    >
                      {state.vendorEmpId || "N/A"}
                    </Typography>
                  </Tooltip>
                </Grid>

                {/* Profile Info Fields */}
                <Grid item xs={12} md={9}>
                  <Grid container spacing={2}>
                    {[
                      { label: "First Name", value: state.firstName },
                      { label: "Last Name", value: state.lastName },
                      { label: "Email", value: state.vendorEmailId },
                      {
                        label: "Mobile",
                        value: `+${state.dialCode} ${state.mobile}`,
                      },
                      { label: "Country", value: state.country },
                    ].map((field, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Typography variant="subtitle2">
                          {field.label}
                        </Typography>
                        <Paper
                          sx={{ p: 1.5, borderRadius: 2, mt: 0.5 }}
                          variant="outlined"
                        >
                          {field.value || "N/A"}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Company Details */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardHeader title="Company Details" style={{ color: "#286a94" }} />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Company Website</Typography>
                  <Paper sx={{ p: 1.5 }} variant="outlined">
                    {state.companyWebsite || "N/A"}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Company Name</Typography>
                  <Paper sx={{ p: 1.5 }} variant="outlined">
                    {state.company || "N/A"}
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2">Company Logo</Typography>
                  <div style={{ marginTop: 10 }}>
                    {userInfo?.companyLogoUrl ? (
                      <img
                        src={userInfo?.companyLogoUrl}
                        alt="Company Logo"
                        style={{
                          width: 150,
                          height: 150,
                          objectFit: "contain",
                          border: "1px solid #ccc",
                          borderRadius: 8,
                          padding: 10,
                        }}
                      />
                    ) : (
                      <Chip label="No Logo Uploaded" color="warning" />
                    )}
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2">Description</Typography>
                  <Paper sx={{ p: 2 }} variant="outlined">
                    {state.description || "No description added"}
                  </Paper>
                </Grid>
              </Grid>

              {/* Locations */}
              {locations
                ?.sort((a, b) => (a.locationType === "HEADQUARTERS" ? -1 : 1))
                ?.map((loc, i) => (
                  <Paper key={i} sx={{ p: 2, mt: 3 }} variant="outlined">
                    <Typography
                      variant="subtitle2"
                      style={{ color: "#286a94" }}
                      sx={{ textTransform: "uppercase", fontWeight: "bold" }}
                    >
                      {loc.locationType}
                    </Typography>
                    <Divider sx={{ my: 1 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">Address</Typography>
                        <Paper sx={{ p: 1.5 }} variant="outlined">
                          {loc.address}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">Country</Typography>
                        <Paper sx={{ p: 1.5 }} variant="outlined">
                          {loc.country}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">State</Typography>
                        <Paper sx={{ p: 1.5 }} variant="outlined">
                          {loc.state || "N/A"}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2">City</Typography>
                        <Paper sx={{ p: 1.5 }} variant="outlined">
                          {loc.city}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Documents */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardHeader title="Documents" style={{ color: "#286a94" }} />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                {[
                  {
                    label: isIndian ? "PAN Number" : "Government Tax ID",
                    value: state.panNumber || state.governmentTaxId,
                    doc: userInfo?.panImageUrl,
                  },
                  {
                    label: "GST Number",
                    value: state.gstNumber,
                    doc: userInfo?.gstImageUrl,
                  },
                  {
                    label: "MSA Document",
                    value: userInfo?.msadocUrl ? "Uploaded" : "NA",
                    doc: userInfo?.msadocUrl,
                  },
                  {
                    label: "NDA Document",
                    value: userInfo?.ndadocUrl ? "Uploaded" : "NA",
                    doc: userInfo?.ndadocUrl,
                  },
                ].map((item, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Typography variant="subtitle2">{item.label}</Typography>
                    <Paper sx={{ p: 1.5 }} variant="outlined">
                      {item.value || "N/A"}
                      {item.doc && (
                        <IconButton
                          size="small"
                          sx={{ float: "right" }}
                          onClick={() => setDialogPreview(item.doc)}
                        >
                          <IoEyeOutline size={20} />
                        </IconButton>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Modal */}
      <Dialog
        open={!!dialogPreview}
        onClose={() => setDialogPreview(null)}
        maxWidth="lg"
        fullWidth
      >
        <IconButton
          sx={{ position: "absolute", color: "red", right: 5, top: 10 }}
          onClick={() => setDialogPreview(null)}
        >
          <CloseIcon />
        </IconButton>

        {dialogPreview && getPreviewType(dialogPreview) === "image" && (
          <img
            src={dialogPreview}
            alt="Preview"
            style={{ width: "100%", padding: 20 }}
          />
        )}

        {dialogPreview && getPreviewType(dialogPreview) === "pdf" && (
          <iframe
            src={dialogPreview}
            title="PDF Preview"
            style={{ width: "100%", height: "80vh", border: "none" }}
          />
        )}

        {dialogPreview && getPreviewType(dialogPreview) === "unknown" && (
          <Typography sx={{ p: 4, textAlign: "center" }}>
            Preview not supported.{" "}
            <a href={dialogPreview} download>
              Download file
            </a>
          </Typography>
        )}
      </Dialog>
    </>
  );
};

export default ViewProfile;
